import { useTheme } from '@mui/material';

/**
 * Swiss/Editorial 디자인 토큰 — `SiteDashboardView` 로컬 사용.
 *
 * 원칙:
 *  - 색상은 최대 3색 (표면/텍스트 외 accent 1, warning 1, danger 1).
 *  - 타이포 스케일은 대비를 크게 (display 3.5rem ↔ body 0.875rem).
 *  - 간격은 Fibonacci-inspired (6/12/24/48/96).
 *  - 모션은 정보 변화를 명확히 알리는 선에서 최소.
 *
 * 프로젝트 전역 테마를 건드리지 않는다. 위젯에서 `useDashboardTokens()`
 * 를 호출해 현재 mode(light|dark)에 맞는 값만 꺼내 `sx` prop 에 사용.
 */

export type DashboardMode = 'light' | 'dark';

export interface DashboardColors {
  bg: string;
  surface: string;
  text: string;
  muted: string;
  border: string;
  accent: string;
  warn: string;
  danger: string;
  success: string;
}

export interface DashboardTypeStyle {
  size: string;
  weight: number;
  spacing: string;
  uppercase?: boolean;
  family?: string;
}

export interface DashboardTypography {
  display: DashboardTypeStyle;
  h1: DashboardTypeStyle;
  h2: DashboardTypeStyle;
  body: DashboardTypeStyle;
  caption: DashboardTypeStyle;
  mono: Pick<DashboardTypeStyle, 'family'>;
}

export interface DashboardSpace {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
}

export interface DashboardMotion {
  fadeIn: string;
  lift: string;
  pulse: string;
}

export interface DashboardTokens {
  colors: Record<DashboardMode, DashboardColors>;
  type: DashboardTypography;
  space: DashboardSpace;
  motion: DashboardMotion;
}

export const dashboardTokens: DashboardTokens = {
  colors: {
    light: {
      bg: '#FAFAF7',
      surface: '#FFFFFF',
      text: '#18181B',
      muted: '#71717A',
      border: '#E4E4E7',
      accent: '#3030FF', // Yves Klein blue — 핵심 CTA 용
      warn: '#F59E0B',
      danger: '#EF4444',
      success: '#10B981',
    },
    dark: {
      bg: '#0A0A0B',
      surface: '#18181B',
      text: '#E7E7E4',
      muted: '#A1A1AA',
      border: '#27272A',
      accent: '#5B5BFF',
      warn: '#FBBF24',
      danger: '#F87171',
      success: '#34D399',
    },
  },
  type: {
    display: { size: '3.5rem', weight: 700, spacing: '-0.03em' },
    h1: { size: '2.25rem', weight: 700, spacing: '-0.02em' },
    h2: { size: '1.5rem', weight: 600, spacing: '-0.01em' },
    body: { size: '0.875rem', weight: 400, spacing: '0' },
    caption: { size: '0.75rem', weight: 500, spacing: '0.08em', uppercase: true },
    mono: { family: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
  },
  space: { xs: 6, sm: 12, md: 24, lg: 48, xl: 96 },
  motion: {
    fadeIn: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)',
    lift: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    pulse: 'background-color 600ms ease-in-out',
  },
} as const;

export interface UseDashboardTokensResult {
  mode: DashboardMode;
  colors: DashboardColors;
  type: DashboardTypography;
  space: DashboardSpace;
  motion: DashboardMotion;
}

/**
 * 현재 theme mode(light|dark) 에 맞는 대시보드 토큰을 반환한다.
 * MUI `useTheme()` 의 `palette.mode` 를 참조하므로 ThemeContext 교체
 * 시 자동으로 재렌더된다.
 */
export function useDashboardTokens(): UseDashboardTokensResult {
  const theme = useTheme();
  const mode: DashboardMode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  return {
    mode,
    colors: dashboardTokens.colors[mode],
    type: dashboardTokens.type,
    space: dashboardTokens.space,
    motion: dashboardTokens.motion,
  };
}
